import React, { useState, useEffect, useRef } from 'react';
import { 
  CreditCard, 
  Plus, 
  X, 
  Upload, 
  Calendar, 
  CheckCircle, 
  AlertTriangle, 
  AlertCircle,
  Trash2,
  Edit2,
  Download,
  Maximize2,
  Bell,
  BellOff,
  Camera,
  Image,
  RefreshCw,
  ExternalLink,
  ArrowLeftRight
} from 'lucide-react';
import axios from 'axios';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Status helper functions
const getCardStatus = (validUntil) => {
  if (!validUntil) return { status: 'unknown', color: 'gray', text: 'Ukjent utløp' };
  
  const now = new Date();
  const expiry = new Date(validUntil);
  const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  
  if (daysUntilExpiry < 0) {
    return { status: 'expired', color: 'red', text: 'Utløpt', icon: AlertCircle };
  } else if (daysUntilExpiry <= 7) {
    return { status: 'critical', color: 'red', text: `${daysUntilExpiry} dager igjen`, icon: AlertCircle };
  } else if (daysUntilExpiry <= 30) {
    return { status: 'warning', color: 'yellow', text: `${daysUntilExpiry} dager igjen`, icon: AlertTriangle };
  } else {
    return { status: 'valid', color: 'green', text: 'Gyldig', icon: CheckCircle };
  }
};

const statusColors = {
  red: 'bg-red-500/20 text-red-400 border-red-500/30',
  yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  green: 'bg-green-500/20 text-green-400 border-green-500/30',
  gray: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

// Fullscreen QR Modal Component
export const WeaponCardFullscreen = ({ card, onClose }) => {
  const status = getCardStatus(card.valid_until);
  
  // Handle ESC key to close
  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  
  const handleDownloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/my-weapon-cards/${card.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vapenkort_${card.weapon_brand}_${card.weapon_model}.pdf`.replace(/ /g, '_');
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center"
      data-testid="weapon-card-fullscreen"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-3 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors z-10"
        data-testid="fullscreen-close-btn"
        title="Lukk (ESC)"
      >
        <X className="h-8 w-8 text-gray-800" />
      </button>
      
      {/* ESC hint */}
      <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-gray-400">
        Trykk ESC eller X for å lukke
      </p>
      
      {/* Content */}
      <div className="flex flex-col items-center justify-center p-4 max-w-lg w-full">
        {/* QR Code */}
        {card.qr_code_url ? (
          <img 
            src={card.qr_code_url} 
            alt="Våpenkort QR-kode"
            className="w-64 h-64 sm:w-80 sm:h-80 object-contain mb-6"
          />
        ) : (
          <div className="w-64 h-64 sm:w-80 sm:h-80 bg-gray-100 flex items-center justify-center mb-6 rounded-lg">
            <p className="text-gray-500">Ingen QR-kode lastet opp</p>
          </div>
        )}
        
        {/* Weapon Info */}
        <div className="text-center space-y-2">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            {card.weapon_type}: {card.weapon_brand} {card.weapon_model}
          </h2>
          <p className="text-lg text-gray-700">Kaliber: {card.caliber}</p>
          {card.valid_until && (
            <p className="text-lg text-gray-700">
              QR-kode gyldig til: {new Date(card.valid_until).toLocaleDateString('nb-NO')}
            </p>
          )}
          
          {/* Status badge */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${statusColors[status.color]}`}>
            {status.icon && <status.icon className="h-5 w-5" />}
            <span className="font-medium">{status.text}</span>
          </div>
        </div>
        
        {/* Download PDF button */}
        <button
          onClick={handleDownloadPDF}
          className="mt-8 flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          data-testid="download-pdf-btn"
        >
          <Download className="h-5 w-5" />
          <span>Last ned PDF</span>
        </button>
      </div>
    </div>
  );
};

// Main MyWeaponCards Component
const MyWeaponCards = () => {
  const { addToast } = useToast();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [fullscreenCard, setFullscreenCard] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    weapon_brand: '',
    weapon_model: '',
    caliber: '',
    weapon_type: 'PISTOL',
    valid_until: '',
    reminder_enabled: true
  });
  const [qrFile, setQrFile] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  
  const weaponTypes = ['PISTOL', 'RIFLE', 'HAGLE', 'REVOLVER', 'ANNET'];
  
  useEffect(() => {
    fetchCards();
  }, []);
  
  const fetchCards = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/my-weapon-cards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCards(response.data.cards || []);
    } catch (error) {
      console.error('Error fetching weapon cards:', error);
      addToast('Kunne ikke hente våpenkort', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateCard = async (e) => {
    e.preventDefault();
    
    if (!formData.weapon_brand || !formData.weapon_model || !formData.caliber) {
      addToast('Fyll ut alle påkrevde felt', 'error');
      return;
    }
    
    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      
      // First create the card
      const response = await axios.post(`${API_BASE}/api/my-weapon-cards`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const newCard = response.data.card;
        
        // If QR file was selected, upload it
        if (qrFile) {
          const formDataUpload = new FormData();
          formDataUpload.append('file', qrFile);
          
          try {
            const qrResponse = await axios.post(
              `${API_BASE}/api/my-weapon-cards/${newCard.id}/upload-qr`,
              formDataUpload,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
            
            if (qrResponse.data.success) {
              newCard.qr_code_url = qrResponse.data.qr_code_url;
            }
          } catch (qrError) {
            console.error('QR upload error:', qrError);
            addToast('Våpenkort opprettet, men QR-opplasting feilet. Du kan laste opp senere.', 'warning');
          }
        }
        
        setCards([newCard, ...cards]);
        setShowAddModal(false);
        resetForm();
        addToast('Våpenkort opprettet!', 'success');
      }
    } catch (error) {
      console.error('Error creating card:', error);
      addToast('Kunne ikke opprette våpenkort', 'error');
    } finally {
      setUploading(false);
    }
  };
  
  const handleUpdateCard = async (e) => {
    e.preventDefault();
    
    if (!editingCard) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/api/my-weapon-cards/${editingCard.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setCards(cards.map(c => c.id === editingCard.id ? { ...c, ...formData } : c));
      setShowEditModal(false);
      setEditingCard(null);
      resetForm();
      addToast('Våpenkort oppdatert', 'success');
    } catch (error) {
      console.error('Error updating card:', error);
      addToast('Kunne ikke oppdatere våpenkort', 'error');
    }
  };
  
  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette våpenkortet?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/api/my-weapon-cards/${cardId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCards(cards.filter(c => c.id !== cardId));
      addToast('Våpenkort slettet', 'success');
    } catch (error) {
      console.error('Error deleting card:', error);
      addToast('Kunne ikke slette våpenkort', 'error');
    }
  };
  
  const handleUploadQR = async (cardId, file) => {
    setUploading(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_BASE}/api/my-weapon-cards/${cardId}/upload-qr`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        // Update local state with new QR URL
        setCards(cards.map(c => 
          c.id === cardId ? { ...c, qr_code_url: response.data.qr_code_url } : c
        ));
        addToast('QR-kode lastet opp!', 'success');
      }
    } catch (error) {
      console.error('Error uploading QR:', error);
      addToast('Kunne ikke laste opp QR-kode', 'error');
    } finally {
      setUploading(false);
    }
  };
  
  const handleFileSelect = (cardId) => (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUploadQR(cardId, file);
    }
  };
  
  // Handle QR file selection in add modal
  const handleQrFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setQrFile(file);
      
      // Create preview - handle both images and SVG
      const reader = new FileReader();
      reader.onloadend = () => {
        // For SVG files, we need to handle them specially
        if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
          // SVG files are read as text, convert to data URL
          const svgContent = reader.result;
          if (typeof svgContent === 'string' && !svgContent.startsWith('data:')) {
            // If read as text, convert to data URL
            const blob = new Blob([svgContent], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(blob);
            setQrPreview(url);
          } else {
            setQrPreview(svgContent);
          }
        } else {
          setQrPreview(reader.result);
        }
      };
      
      // Read SVG as text to preserve it, others as data URL
      if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
        reader.readAsDataURL(file);
      } else {
        reader.readAsDataURL(file);
      }
    }
  };
  
  // Handle weapon image upload
  const handleUploadWeaponImage = async (cardId, file) => {
    setUploading(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_BASE}/api/my-weapon-cards/${cardId}/upload-weapon-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        setCards(cards.map(c => 
          c.id === cardId ? { ...c, weapon_image_url: response.data.weapon_image_url } : c
        ));
        addToast('Våpenbilde oppdatert!', 'success');
      }
    } catch (error) {
      console.error('Error uploading weapon image:', error);
      addToast('Kunne ikke laste opp våpenbilde', 'error');
    } finally {
      setUploading(false);
    }
  };
  
  const handleWeaponImageSelect = (cardId) => (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUploadWeaponImage(cardId, file);
    }
  };
  
  // Toggle between QR code and weapon image as preview
  // If no weapon image exists, trigger search for one
  const handleTogglePreview = async (cardId) => {
    try {
      const token = localStorage.getItem('token');
      const card = cards.find(c => c.id === cardId);
      
      // If switching to weapon image view but no image exists, trigger search
      if (card && !card.weapon_image_url && !card.prefer_qr_preview) {
        addToast('Søker etter bilde av våpenet...', 'info');
        
        // Call backend to search for weapon image
        try {
          const searchResponse = await axios.post(
            `${API_BASE}/api/my-weapon-cards/${cardId}/search-image`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (searchResponse.data.success && searchResponse.data.weapon_image_url) {
            // Update local state with new image
            setCards(cards.map(c => 
              c.id === cardId ? { 
                ...c, 
                weapon_image_url: searchResponse.data.weapon_image_url,
                prefer_qr_preview: true  // Show the weapon image
              } : c
            ));
            addToast('Bilde funnet!', 'success');
            return;
          } else {
            addToast('Fant ikke bilde av dette våpenet', 'warning');
            return;
          }
        } catch (searchError) {
          console.error('Image search failed:', searchError);
          addToast('Kunne ikke søke etter bilde', 'error');
          return;
        }
      }
      
      // Toggle the preference
      const response = await axios.post(
        `${API_BASE}/api/my-weapon-cards/${cardId}/toggle-preview`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Update local state
        setCards(cards.map(c => 
          c.id === cardId ? { ...c, prefer_qr_preview: response.data.prefer_qr_preview } : c
        ));
      }
    } catch (error) {
      console.error('Error toggling preview:', error);
      addToast('Kunne ikke bytte visning', 'error');
    }
  };
  
  const openEditModal = (card) => {
    setEditingCard(card);
    setFormData({
      weapon_brand: card.weapon_brand || '',
      weapon_model: card.weapon_model || '',
      caliber: card.caliber || '',
      weapon_type: card.weapon_type || 'PISTOL',
      valid_until: card.valid_until ? card.valid_until.split('T')[0] : '',
      reminder_enabled: card.reminder_enabled !== false
    });
    setShowEditModal(true);
  };
  
  const resetForm = () => {
    setFormData({
      weapon_brand: '',
      weapon_model: '',
      caliber: '',
      weapon_type: 'PISTOL',
      valid_until: '',
      reminder_enabled: true
    });
    setQrFile(null);
    setQrPreview(null);
  };
  
  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6" data-testid="weapon-cards-loading">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="h-6 w-6 text-yellow-400" />
          <h2 className="text-xl font-bold text-white">Mine Våpenkort</h2>
        </div>
        <p className="text-gray-400">Laster våpenkort...</p>
      </div>
    );
  }
  
  return (
    <>
      <div className="bg-gray-800 rounded-lg p-4 sm:p-6" data-testid="my-weapon-cards">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <CreditCard className="h-6 w-6 text-yellow-400" />
            <div>
              <h2 className="text-xl font-bold text-white">Mine Våpenkort</h2>
              <p className="text-sm text-gray-400">Lagre QR-kodene dine for rask tilgang</p>
            </div>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-medium rounded-lg transition-colors"
            data-testid="add-weapon-card-btn"
          >
            <Plus className="h-5 w-5" />
            <span>Legg til våpenkort</span>
          </button>
        </div>
        
        {/* Info box with link to våpenregisteret */}
        <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-200 mb-3">
            Denne seksjonen lagrer kun QR-koder du selv laster opp. Ingen integrasjon mot Nasjonalt våpenregister.
            Vis QR-koden i fullskjerm når du kjøper ammunisjon.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-2 border-t border-blue-700/30">
            <a
              href="https://vaapenregisteret.brreg.no/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 text-sm font-medium"
            >
              <ExternalLink className="h-4 w-4" />
              Gå til våpenregisteret (Altinn.no)
            </a>
            <span className="text-xs text-gray-400">
              Husk å klikke på "Deling"-knappen for å se QR-koden
            </span>
          </div>
        </div>
        
        {/* Cards Grid */}
        {cards.length === 0 ? (
          <div className="text-center py-12">
            <CreditCard className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-2">Ingen våpenkort lagt til ennå</p>
            <p className="text-sm text-gray-500">Klikk "Legg til våpenkort" for å komme i gang</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map((card) => {
              const status = getCardStatus(card.valid_until);
              return (
                <div
                  key={card.id}
                  className="bg-gray-700 rounded-lg p-4 border border-gray-600"
                  data-testid={`weapon-card-${card.id}`}
                >
                  {/* Preview Area - Default is QR code, swap to weapon image */}
                  <div className="relative h-32 bg-gray-600 rounded-lg mb-3 overflow-hidden group">
                    {/* Show QR code by default if available and not preferring weapon image */}
                    {card.qr_code_url && !card.prefer_qr_preview ? (
                      // QR code preview (default)
                      <div 
                        className="w-full h-full bg-white flex items-center justify-center cursor-pointer relative"
                        onClick={() => setFullscreenCard(card)}
                      >
                        <img 
                          src={card.qr_code_url} 
                          alt="QR-kode"
                          className="max-w-full max-h-full object-contain p-2"
                        />
                        {/* Swap button - search for weapon image */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleTogglePreview(card.id); }}
                          className="absolute top-2 right-2 p-1.5 bg-gray-800/80 hover:bg-gray-700 rounded-full text-white transition-colors z-20"
                          title="Søk etter våpenbilde"
                        >
                          <ArrowLeftRight className="h-4 w-4" />
                        </button>
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none z-10">
                          <Maximize2 className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    ) : card.weapon_image_url && card.prefer_qr_preview ? (
                      // Weapon image preview (after swap)
                      <>
                        <img 
                          src={card.weapon_image_url} 
                          alt={`${card.weapon_brand} ${card.weapon_model}`}
                          className="w-full h-full object-contain bg-white"
                        />
                        {/* Swap button - back to QR */}
                        {card.qr_code_url && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleTogglePreview(card.id); }}
                            className="absolute top-2 right-2 p-1.5 bg-gray-800/80 hover:bg-gray-700 rounded-full text-white transition-colors z-20"
                            title="Bytt til QR-kode"
                          >
                            <ArrowLeftRight className="h-4 w-4" />
                          </button>
                        )}
                        <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer z-10">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleWeaponImageSelect(card.id)}
                            className="hidden"
                            disabled={uploading}
                          />
                          <div className="flex items-center gap-2 text-white text-sm">
                            <RefreshCw className="h-5 w-5" />
                            <span>Bytt bilde</span>
                          </div>
                        </label>
                      </>
                    ) : card.qr_code_url ? (
                      // QR code only (no weapon image yet, prefer_qr_preview=true means user tried swap but no image found)
                      <div 
                        className="w-full h-full bg-white flex items-center justify-center cursor-pointer relative"
                        onClick={() => setFullscreenCard(card)}
                      >
                        <img 
                          src={card.qr_code_url} 
                          alt="QR-kode"
                          className="max-w-full max-h-full object-contain p-2"
                        />
                        {/* Swap button - search for weapon image */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleTogglePreview(card.id); }}
                          className="absolute top-2 right-2 p-1.5 bg-gray-800/80 hover:bg-gray-700 rounded-full text-white transition-colors z-20"
                          title="Søk etter våpenbilde"
                        >
                          <ArrowLeftRight className="h-4 w-4" />
                        </button>
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none z-10">
                          <Maximize2 className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    ) : (
                      // No QR code uploaded yet - show upload prompt
                      <label className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-500/50 transition-colors">
                        <input
                          type="file"
                          accept="image/*,.svg,image/svg+xml"
                          onChange={handleFileSelect(card.id)}
                          className="hidden"
                          disabled={uploading}
                        />
                        <Camera className="h-8 w-8 text-gray-400 mb-1" />
                        <span className="text-xs text-gray-400">Last opp QR-kode</span>
                      </label>
                    )}
                  </div>
                  
                  {/* Card Info */}
                  <div className="space-y-2 pt-1">
                    <h3 className="font-bold text-white text-base leading-tight">
                      {card.weapon_brand} {card.weapon_model}
                    </h3>
                    <p className="text-sm text-gray-300">{card.weapon_type} • {card.caliber}</p>
                    
                    {/* Status */}
                    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border ${statusColors[status.color]}`}>
                      {status.icon && <status.icon className="h-3.5 w-3.5" />}
                      <span>{status.text}</span>
                    </div>
                    
                    {/* Valid until */}
                    {card.valid_until && (
                      <p className="text-sm text-gray-300">
                        Gyldig til: {new Date(card.valid_until).toLocaleDateString('nb-NO')}
                      </p>
                    )}
                    
                    {/* Reminder indicator */}
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      {card.reminder_enabled ? (
                        <><Bell className="h-3 w-3" /> Påminnelse på</>
                      ) : (
                        <><BellOff className="h-3 w-3" /> Påminnelse av</>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-600">
                    {card.qr_code_url && (
                      <button
                        onClick={() => setFullscreenCard(card)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-lg text-sm transition-colors"
                        data-testid={`fullscreen-btn-${card.id}`}
                      >
                        <Maximize2 className="h-4 w-4" />
                        <span className="hidden sm:inline">Vis</span>
                      </button>
                    )}
                    
                    {!card.qr_code_url && (
                      <label className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-sm cursor-pointer transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect(card.id)}
                          className="hidden"
                          disabled={uploading}
                        />
                        <Upload className="h-4 w-4" />
                        <span className="hidden sm:inline">Last opp</span>
                      </label>
                    )}
                    
                    <button
                      onClick={() => openEditModal(card)}
                      className="p-2 hover:bg-gray-600 rounded-lg transition-colors"
                      title="Rediger"
                    >
                      <Edit2 className="h-4 w-4 text-gray-400" />
                    </button>
                    
                    <button
                      onClick={() => handleDeleteCard(card.id)}
                      className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                      title="Slett"
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-lg w-full max-w-md max-h-[90vh] flex flex-col">
            {/* Fixed header */}
            <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-700">
              <h3 className="text-xl font-bold text-white">Legg til våpenkort</h3>
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="text-gray-400 hover:text-white">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-6 pt-4">
              <form onSubmit={handleCreateCard} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Våpentype *</label>
                  <select
                    value={formData.weapon_type}
                    onChange={(e) => setFormData({ ...formData, weapon_type: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  >
                    {weaponTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Våpenmerke *</label>
                  <input
                    type="text"
                    value={formData.weapon_brand}
                    onChange={(e) => setFormData({ ...formData, weapon_brand: e.target.value })}
                    placeholder="f.eks. SMITH & WESSON"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Modellnavn *</label>
                  <input
                    type="text"
                    value={formData.weapon_model}
                    onChange={(e) => setFormData({ ...formData, weapon_model: e.target.value })}
                    placeholder="f.eks. M&P9L CORE"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Kaliber *</label>
                  <input
                    type="text"
                    value={formData.caliber}
                    onChange={(e) => setFormData({ ...formData, caliber: e.target.value })}
                    placeholder="f.eks. 9mm"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">QR-kode gyldig til</label>
                  <input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                </div>
                
                {/* QR Code Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    QR-kode fra Altinn.no
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Last opp bildet av QR-koden du har hentet fra Altinn.no
                  </p>
                  
                  {qrPreview ? (
                    <div className="relative border-2 border-green-500 rounded-lg p-2 bg-white">
                      <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                      <img 
                        src={qrPreview} 
                        alt="QR Preview" 
                        className="w-full max-h-48 object-contain"
                      />
                      <button
                        type="button"
                        onClick={() => { setQrFile(null); setQrPreview(null); }}
                        className="absolute bottom-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 rounded-full text-white shadow-lg"
                        title="Fjern QR-kode"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <p className="text-center text-xs text-green-600 mt-2 font-medium">
                        QR-kode valgt og klar til opplasting
                      </p>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center h-32 bg-gray-700 hover:bg-gray-600 border-2 border-dashed border-gray-500 rounded-lg cursor-pointer transition-colors">
                      <input
                        type="file"
                        accept="image/*,.svg,image/svg+xml"
                        onChange={handleQrFileSelect}
                        className="hidden"
                      />
                      <Camera className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-400">Velg eller ta bilde av QR-kode</span>
                      <span className="text-xs text-gray-500 mt-1">Støtter PNG, JPG og SVG</span>
                    </label>
                  )}
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="reminder_enabled"
                    checked={formData.reminder_enabled}
                    onChange={(e) => setFormData({ ...formData, reminder_enabled: e.target.checked })}
                    className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-yellow-500"
                  />
                  <label htmlFor="reminder_enabled" className="text-sm text-gray-300">
                    Påminn meg før utløp
                  </label>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowAddModal(false); resetForm(); }}
                    className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="flex-1 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {uploading ? 'Oppretter...' : 'Opprett'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Modal */}
      {showEditModal && editingCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Rediger våpenkort</h3>
              <button onClick={() => { setShowEditModal(false); setEditingCard(null); resetForm(); }} className="text-gray-400 hover:text-white">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleUpdateCard} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Våpentype</label>
                <select
                  value={formData.weapon_type}
                  onChange={(e) => setFormData({ ...formData, weapon_type: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                >
                  {weaponTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Våpenmerke</label>
                <input
                  type="text"
                  value={formData.weapon_brand}
                  onChange={(e) => setFormData({ ...formData, weapon_brand: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Modellnavn</label>
                <input
                  type="text"
                  value={formData.weapon_model}
                  onChange={(e) => setFormData({ ...formData, weapon_model: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Kaliber</label>
                <input
                  type="text"
                  value={formData.caliber}
                  onChange={(e) => setFormData({ ...formData, caliber: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">QR-kode gyldig til</label>
                <input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="edit_reminder_enabled"
                  checked={formData.reminder_enabled}
                  onChange={(e) => setFormData({ ...formData, reminder_enabled: e.target.checked })}
                  className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-yellow-500"
                />
                <label htmlFor="edit_reminder_enabled" className="text-sm text-gray-300">
                  Påminn meg før utløp
                </label>
              </div>
              
              {/* QR Upload for existing card */}
              <div className="pt-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">QR-kode bilde</label>
                {editingCard.qr_code_url ? (
                  <div className="flex items-center gap-4">
                    <img src={editingCard.qr_code_url} alt="QR" className="w-20 h-20 object-contain bg-gray-700 rounded" />
                    <label className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg cursor-pointer text-sm text-gray-300 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect(editingCard.id)}
                        className="hidden"
                        disabled={uploading}
                      />
                      <Upload className="h-4 w-4" />
                      Bytt bilde
                    </label>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 px-4 py-8 bg-gray-700 hover:bg-gray-600 rounded-lg cursor-pointer border-2 border-dashed border-gray-600 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect(editingCard.id)}
                      className="hidden"
                      disabled={uploading}
                    />
                    <Camera className="h-6 w-6 text-gray-400" />
                    <span className="text-gray-400">Last opp QR-kode</span>
                  </label>
                )}
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setEditingCard(null); resetForm(); }}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-medium rounded-lg transition-colors"
                >
                  Lagre
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Fullscreen Modal */}
      {fullscreenCard && (
        <WeaponCardFullscreen 
          card={fullscreenCard} 
          onClose={() => setFullscreenCard(null)} 
        />
      )}
    </>
  );
};

export default MyWeaponCards;
